import React from 'react';
import styled from 'styled-components';
import ModalCenter from '@/common/components/modal/ModalCenter';
import { LabelInput } from '@/common/components/input/LabelInput';
import { Button } from '@/common/components/button/Button';
import { semanticColors } from '@/common/styles/colorTokens';
import { ShieldCheck, AlertCircle, Loader2 } from 'lucide-react';
import { typography } from '@/common/styles/typographyTokens';

interface PasswordChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  newPassword: string;
  confirmPassword: string;
  passwordError: string;
  confirmPasswordError: string;
  isUpdating: boolean;
  onNewPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  onPasswordBlur: () => void;
  onConfirmPasswordBlur: () => void;
  onSubmit: () => void;
}

const PasswordChangeModal: React.FC<PasswordChangeModalProps> = ({
  isOpen,
  onClose,
  newPassword,
  confirmPassword,
  passwordError,
  confirmPasswordError,
  isUpdating,
  onNewPasswordChange,
  onConfirmPasswordChange,
  onPasswordBlur,
  onConfirmPasswordBlur,
  onSubmit,
}) => {
  return (
    <ModalCenter
      isOpen={isOpen}
      onClose={onClose}
      title="비밀번호 변경"
      maxWidth="480px"
      footer={
        <ModalButtonContainer>
          <Button
            $variant="outline"
            $size="md"
            onClick={onClose}
            disabled={isUpdating}
            style={{ flex: 1 }}
          >
            취소
          </Button>
          <Button
            $variant="fill"
            $size="md"
            onClick={onSubmit}
            disabled={isUpdating || !!passwordError || !!confirmPasswordError || !newPassword || !confirmPassword}
            style={{ flex: 1 }}
          >
            {isUpdating ? (
              <>
                <Loader2 size={18} className="animate-spin" style={{ marginRight: '8px' }} />
                변경 중...
              </>
            ) : '비밀번호 변경'}
          </Button>
        </ModalButtonContainer>
      }
    >
      <ModalContentContainer>
        <InfoBox>
          <ShieldCheck size={20} color={semanticColors.text.brand} />
          <p>보안을 위해 <strong>8자 이상, 영문/숫자/특수문자 중 2가지 이상</strong>을 조합하여 설정해 주세요.</p>
        </InfoBox>

        <InputSection>
          <LabelInput
            label="새 비밀번호"
            type="password"
            value={newPassword}
            onChange={(e) => onNewPasswordChange(e.target.value)}
            onBlur={onPasswordBlur}
            error={!!passwordError}
            errorMessage={passwordError}
            placeholder="새 비밀번호를 입력하세요"
            disabled={isUpdating}
          />
        </InputSection>

        <InputSection>
          <LabelInput
            label="비밀번호 확인"
            type="password"
            value={confirmPassword}
            onChange={(e) => onConfirmPasswordChange(e.target.value)}
            onBlur={onConfirmPasswordBlur}
            error={!!confirmPasswordError}
            errorMessage={confirmPasswordError}
            placeholder="비밀번호를 다시 한번 입력하세요"
            disabled={isUpdating}
          />
        </InputSection>
      </ModalContentContainer>
    </ModalCenter>
  );
};

const ModalContentContainer = styled.div`
  width: 100%;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InfoBox = styled.div`
  display: flex;
  gap: 12px;
  padding: 1rem;
  background: ${semanticColors.background.tertiary};
  border-radius: 12px;
  border: 1px solid ${semanticColors.border.primary};

  p {
    margin: 0;
    ${typography.caption.caption1.default}
    color: ${semanticColors.text.secondary};
    line-height: 1.5;
    
    strong {
      color: ${semanticColors.text.primary};
      font-weight: 600;
    }
  }
`;

const InputSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ModalButtonContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 0 1.5rem 1.5rem;
`;

export default PasswordChangeModal;
